import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { Countdown } from "./Countdown";
import { AddToCalendar } from "./AddToCalendar";
import { imgSrc } from "@/lib/img";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      weekday: "long", year: "numeric", month: "long", day: "numeric",
    });
  } catch { return iso; }
};

export const Hero = ({ content }) => {
  const { couple, hero, wedding_date, tagline, date_label } = content;
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const ov = typeof hero?.overlay_opacity === "number" ? hero.overlay_opacity : 0.55;
  const travelEnabled = content.sections?.travel !== false;
  const heroPos = hero?.bg_position || "center";
  const heroZoom = typeof hero?.bg_zoom === "number" ? hero.bg_zoom : 1;

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${imgSrc(hero?.background_image)})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: heroPos,
          transform: `scale(${heroZoom})`,
          transformOrigin: heroPos,
        }}
      />
      {/* Legibility scrims: vertical + desktop horizontal + vignette */}
      <div
        className="absolute inset-0"
        style={{ background: `linear-gradient(to top, rgba(0,0,0,${Math.min(ov + 0.3, 0.95)}), rgba(0,0,0,${ov}) 45%, rgba(0,0,0,${Math.max(ov - 0.2, 0.12)}))` }}
      />
      <div
        className="absolute inset-0 hidden md:block"
        style={{ background: `linear-gradient(to right, rgba(0,0,0,${ov}) 0%, rgba(0,0,0,${Math.max(ov - 0.3, 0.08)}) 55%, rgba(0,0,0,0) 100%)` }}
      />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, rgba(0,0,0,0) 25%, rgba(0,0,0,0.5) 100%)" }} />

      <div className="relative z-10 text-center px-6 max-w-3xl hero-text-shadow">
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          className="font-script text-3xl sm:text-4xl text-[#F8E9C8] mb-4"
          data-testid="hero-subtitle"
        >
          {hero?.subtitle}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.15 }}
          className="font-serif font-medium text-white text-5xl sm:text-7xl lg:text-8xl leading-none"
          data-testid="hero-couple-names"
        >
          {couple?.partner1} <span className="italic text-[#F2D9CB]">&</span> {couple?.partner2}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-6 flex items-center justify-center gap-4"
        >
          <span className="h-px w-12 bg-white/70" />
          <p className="text-white text-sm sm:text-base tracking-[0.15em] uppercase" data-testid="hero-date">
            {date_label || fmtDate(wedding_date)}
          </p>
          <span className="h-px w-12 bg-white/70" />
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.28 }}
          className="mt-4 text-white text-sm max-w-lg mx-auto"
        >
          {tagline}
        </motion.p>

        {content.hashtag && (
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.32 }}
            className="mt-3 text-[#F8E9C8] text-sm tracking-[0.2em]"
            data-testid="hero-hashtag"
          >
            {content.hashtag}
          </motion.p>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.35 }}
          className="mt-10"
        >
          <Countdown date={wedding_date} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.45 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-3"
        >
          <button
            onClick={() => scrollTo("rsvp")}
            data-testid="hero-rsvp-button"
            className="rounded-full px-8 py-3 text-sm font-medium transition-all hover:scale-105 shadow-lg"
            style={{ backgroundColor: "var(--gold)", color: "var(--on-gold)" }}
          >
            RSVP to our celebration
          </button>
          <AddToCalendar content={content} variant="light" />
          {hero?.directions_label && travelEnabled && (
            <button
              onClick={() => scrollTo("travel")}
              data-testid="hero-directions-button"
              className="rounded-full px-6 py-3 text-sm font-medium transition-all hover:scale-105 border flex items-center gap-2"
              style={{ borderColor: "rgba(255,255,255,0.6)", color: "#fff", backgroundColor: "rgba(255,255,255,0.1)" }}
            >
              <MapPin className="w-4 h-4" /> {hero.directions_label}
            </button>
          )}
        </motion.div>
      </div>
    </section>
  );
};
